import React, { useState, useEffect } from "react";
import { adminApi } from "../services/api";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { formatPercentage } from "../utils";

const CHART_COLORS = [
  "#3b82f6", "#10b981", "#8b5cf6", "#ef4444", "#f59e0b", "#06b6d4",
  "#ec4899", "#84cc16", "#6366f1", "#f97316", "#14b8a6", "#a855f7",
  "#eab308", "#64748b",
];

const DashboardPage = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Graph data states
  const [apiUsagePeriod, setApiUsagePeriod] = useState("monthly");
  const [apiUsageData, setApiUsageData] = useState([]);

  const [apiCallsPeriod, setApiCallsPeriod] = useState("monthly");
  const [apiCallsData, setApiCallsData] = useState([]);

  const [errorSuccessRatioPeriod, setErrorSuccessRatioPeriod] = useState("monthly");
  const [errorSuccessRatioData, setErrorSuccessRatioData] = useState([]);

  const [entityStatsPeriod, setEntityStatsPeriod] = useState("monthly");
  const [entityStatsData, setEntityStatsData] = useState([]);

  const [moduleStatsPeriod, setModuleStatsPeriod] = useState("monthly");
  const [moduleStatsData, setModuleStatsData] = useState([]);
  const [moduleStatsLoading, setModuleStatsLoading] = useState(true);

  useEffect(() => {
    fetchOverview();
  }, []);

  useEffect(() => {
    fetchApiUsage();
  }, [apiUsagePeriod]);

  useEffect(() => {
    fetchApiCalls();
  }, [apiCallsPeriod]);

  useEffect(() => {
    fetchErrorSuccessRatio();
  }, [errorSuccessRatioPeriod]);

  useEffect(() => {
    fetchEntityStats();
  }, [entityStatsPeriod]);

  useEffect(() => {
    fetchModuleStats();
  }, [moduleStatsPeriod]);

  const fetchOverview = async () => {
    try {
      const response = await adminApi.getAnalyticsOverview();
      setOverview(response.data.overview);
    } catch (err) {
      console.error("Failed to fetch overview:", err);
      setError("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const fetchApiUsage = async () => {
    try {
      const response = await adminApi.getApiUsage(apiUsagePeriod);
      setApiUsageData(response.data.data);
    } catch (err) {
      console.error("Failed to fetch API usage:", err);
    }
  };

  const fetchApiCalls = async () => {
    try {
      const response = await adminApi.getApiCalls(apiCallsPeriod);
      setApiCallsData(response.data.data);
    } catch (err) {
      console.error("Failed to fetch API calls:", err);
    }
  };

  const fetchErrorSuccessRatio = async () => {
    try {
      const response = await adminApi.getApiUsage(errorSuccessRatioPeriod);
      setErrorSuccessRatioData(response.data.data);
    } catch (err) {
      console.error("Failed to fetch error/success ratio:", err);
    }
  };

  const fetchEntityStats = async () => {
    try {
      console.log("fetchEntityStats request params:", { period: entityStatsPeriod });
      const response = await adminApi.getAuditLogStats({ period: entityStatsPeriod });
      console.log("fetchEntityStats response:", response.data);
      setEntityStatsData(response.data.stats.entityStats || []);
    } catch (err) {
      console.error("Failed to fetch entity stats:", err);
    }
  };

  const fetchModuleStats = async () => {
    setModuleStatsLoading(true);
    try {
      const response = await adminApi.getModuleStats({ period: moduleStatsPeriod });
      setModuleStatsData(response.data.moduleStats || []);
    } catch (err) {
      console.error("Failed to fetch module stats:", err);
    } finally {
      setModuleStatsLoading(false);
    }
  };

  const PeriodSelector = ({ value, onChange }) => (
    <div className="flex border border-line rounded-lg overflow-hidden bg-white">
      {["weekly", "monthly", "yearly"].map((period) => (
        <button
          key={period}
          onClick={() => onChange(period)}
          className={`border-0 py-2 px-4 text-sm font-medium cursor-pointer border-r border-line last:border-r-0 transition-all ${
            value === period ? "bg-[#1e293b] text-white" : "text-muted hover:bg-soft "
          }`}
        >
          {period.charAt(0).toUpperCase() + period.slice(1)}
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
        <div className="py-16 text-center">
          <div className="inline-block w-8 h-8 border-3 border-line border-t-indigo rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
        <div className="py-16 text-center text-red-600">{error}</div>
      </div>
    );
  }

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
      <div className="mb-4">
        <h1 className="text-[28px] font-bold tracking-tight m-0">Dashboard</h1>
        <p className="text-muted text-[15px] mt-1 mb-0">{getGreeting()}, Admin! Monitor API usage, user growth, and system performance metrics.</p>
      </div>

      {/* State Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Total API Calls</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight">{overview?.totalApiCalls || 0}</div>
        </div>
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Failed API Calls</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight text-red-700">{overview?.totalFailedApiCalls || 0}</div>
        </div>
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Error Rate</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight text-yellow-700">{formatPercentage(overview?.errorRate || 0)}%</div>
        </div>
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Success Rate</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight text-green-700">{formatPercentage(100 - (overview?.errorRate || 0))}%</div>
        </div>
      </div>

      {/* Graphs */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* API Usage */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">API Usage</h3>
            <PeriodSelector value={apiUsagePeriod} onChange={setApiUsagePeriod} />
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={apiUsageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                <Bar dataKey="successCount" stackId="a" fill="#10b981" name="Success" radius={[0, 0, 0, 0]} />
                <Bar dataKey="errorCount" stackId="a" fill="#ef4444" name="Error" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* API Calls */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">API Calls</h3>
            <PeriodSelector value={apiCallsPeriod} onChange={setApiCallsPeriod} />
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={apiCallsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#8b5cf6"
                  strokeWidth={2.5}
                  dot={{ fill: "#8b5cf6", strokeWidth: 0, r: 5 }}
                  activeDot={{ r: 7 }}
                  name="API Calls"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Activity by Entity */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">API Calls By Module</h3>
            <PeriodSelector value={entityStatsPeriod} onChange={setEntityStatsPeriod} />
          </div>
          <div style={{ height: Math.max(300, entityStatsData.length * 32) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={entityStatsData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} width={110} interval={0} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Activity">
                  {entityStatsData.map((entry, index) => (
                    <Cell key={entry.entity || index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Module Retrieval Counts */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">Retrival Counts By Module</h3>
            <PeriodSelector value={moduleStatsPeriod} onChange={setModuleStatsPeriod} />
          </div>
          {moduleStatsLoading ? (
            <div className="py-16 text-center">
              <div className="inline-block w-8 h-8 border-3 border-line border-t-indigo rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={moduleStatsData} margin={{ bottom: 40, left: 20, right: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    dataKey="name"
                    stroke="#6b7280"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "12px",
                      padding: "8px 12px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#3b82f6"
                    strokeWidth={2.5}
                    dot={{ fill: "#3b82f6", strokeWidth: 0, r: 4 }}
                    activeDot={{ r: 7 }}
                    name="Retrieval Counts"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Error & Success Ratio Trend */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">Error & Success Ratio Trend</h3>
            <PeriodSelector value={errorSuccessRatioPeriod} onChange={setErrorSuccessRatioPeriod} />
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={errorSuccessRatioData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                <Line
                  type="monotone"
                  dataKey="successCount"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  dot={{ fill: "#10b981", strokeWidth: 0, r: 5 }}
                  activeDot={{ r: 7 }}
                  name="Success"
                />
                <Line
                  type="monotone"
                  dataKey="errorCount"
                  stroke="#ef4444"
                  strokeWidth={2.5}
                  dot={{ fill: "#ef4444", strokeWidth: 0, r: 5 }}
                  activeDot={{ r: 7 }}
                  name="Error"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Error & Success Ratio */}
        {/* <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">Error & Success Ratio</h3>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'Success', value: overview?.totalApiCalls - overview?.totalFailedApiCalls || 0 },
                    { name: 'Error', value: overview?.totalFailedApiCalls || 0 }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  <Cell fill="#10b981" />
                  <Cell fill="#ef4444" />
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default DashboardPage;
